import { useQueries, useQuery } from '@tanstack/react-query'

import {
  OrganisationServiceClient,
  OrganisationUser,
} from '@droidmap/organisation-service-contract'
import { SUPPORT_USER_ID } from '@droidmap/permissions-service-contract'
import { DroidMapMetadata } from '@droidmap/shared-service-contract'
import { User, UserServiceClient } from '@droidmap/user-service-contract'

/**
 * Custom hook to fetch organisation users and build a userId -> User map for all referenced user IDs.
 */
export function useOrganisationUsersWithUserMap({
  orgId,
  organisationClient,
  userClient,
  metadata,
}: {
  orgId: string
  organisationClient: OrganisationServiceClient
  userClient: UserServiceClient
  metadata: DroidMapMetadata
}) {
  // 1. Fetch organisation users
  const {
    data: orgUsers = [],
    isLoading: isLoadingOrgUsers,
    ...usersQueryRest
  } = useQuery<OrganisationUser[]>({
    queryKey: ['organisationUsers', orgId],
    queryFn: async () => {
      // Name the org being listed. Without it the request goes out under the
      // client's default org (`system`), which DroidMap support's grants do
      // not count for (droidmap#2651), so an assist session listed no one and
      // showed every creator as Unknown.
      const response =
        await organisationClient.listOrganisationUsersByOrganisation(
          { orgId },
          { ...metadata, orgId },
        )
      return response.items
    },
  })

  // 2. Extract all unique user IDs from userId, createdBy, updatedBy. Not
  // DroidMap Support's (droidmap#2651), which has no user record to fetch:
  // `userFor` names it without one.
  const userIds = Array.from(
    new Set(
      orgUsers
        .flatMap((u) => [u.userId, u.createdBy, u.updatedBy])
        .filter((id) => id && id !== SUPPORT_USER_ID),
    ),
  ) as string[]

  // 3. Fetch all user records in parallel
  const userQueries = useQueries({
    queries: userIds.map((id) => ({
      queryKey: ['user', id],
      queryFn: () => userClient.getUser({ id }, metadata),
      enabled: !!id,
    })),
  })

  // 4. Build userId -> User map
  const userMap: Record<string, User> = {}
  userQueries.forEach((q, i) => {
    if (q.data) {
      userMap[userIds[i]] = q.data
    }
  })

  const isLoadingUserMap = userQueries.some((q) => q.isLoading)

  return {
    orgUsers,
    userMap,
    isLoading: isLoadingOrgUsers || isLoadingUserMap,
    isLoadingOrgUsers,
    isLoadingUserMap,
    ...usersQueryRest,
  }
}
